/**
 * ka-cart-drawer.js
 * KickAss Products — Custom Cart Drawer
 *
 * What this script does (and does NOT do):
 * ─────────────────────────────────────────
 * The theme's existing custom elements handle the heavy lifting:
 *   - <product-form>  intercepts the PDP add-to-cart form, calls /cart/add.js
 *                     with bundled sections, then calls CartDrawer.open()
 *   - <product-card>  handles collection card ATC buttons the same way
 *   - <cart-drawer>   handles open/close, Escape key, outside-click,
 *                     and quantity changes via .qty input change events
 *
 * This script adds the things the theme's CartDrawer does NOT handle:
 *   1. +/- quantity buttons (the theme only handles direct input changes)
 *   2. Product Recommendations — fetched via Section Rendering API so that
 *      Liquid handles all club pricing logic server-side.
 *   3. Recommendation "Add to cart" — passes `properties[_powerx_custom_price]`
 *      when `data-club-price` is present, matching the theme's product-card
 *      club pricing contract.
 *   4. Standard Storefront Events (shopify:cart:view, shopify:cart:lines-update)
 *      for analytics compatibility.
 *   5. Loading overlay — shown during cart mutations so the customer has
 *      visual feedback on slow connections.
 *
 * We deliberately do NOT re-implement add-to-cart or section re-rendering —
 * the theme's JS already does that correctly.
 */

(function () {
  'use strict';

  const ROOT = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ── Standard Storefront Events ─────────────────────────────────────── */
  /*
   * The Standard Events library is loaded as an ES module in the head snippet
   * and assigned to window.Shopify.StandardEvents. We wait for it here with a
   * short poll so we don't miss it if the module resolves slightly after this
   * script runs. All SE usage is wrapped in try/catch so a CDN failure is
   * non-fatal.
   */

  let SE = null;

  function getSE() {
    if (SE) return SE;
    SE = (window.Shopify && window.Shopify.StandardEvents) || null;
    return SE;
  }

  function dispatchCartViewEvent() {
    const se = getSE();
    if (!se || !se.CartViewEvent) return;
    try {
      const countEl = document.querySelector('.thb-secondary-area--item-count');
      const itemCount = parseInt((countEl && countEl.textContent) || '0', 10);
      const drawerEl = document.getElementById('Cart-Drawer');
      if (drawerEl) {
        drawerEl.dispatchEvent(new se.CartViewEvent({
          context: 'dialog',
          cart: itemCount > 0 ? { totalQuantity: itemCount } : null,
        }));
      }
    } catch (e) { /* non-fatal */ }
  }

  /* ── Loading overlay ────────────────────────────────────────────────── */
  /*
   * The theme's CartDrawer manages its own internal loading state but does not
   * toggle our ka-cart-loading overlay. We show/hide it around our own AJAX
   * calls (rec add) so the customer has visual feedback.
   */

  function showLoading() {
    const el = document.querySelector('[data-cart-loading]');
    if (el) {
      el.setAttribute('aria-hidden', 'false');
      el.style.display = 'flex';
    }
  }

  function hideLoading() {
    const el = document.querySelector('[data-cart-loading]');
    if (el) {
      el.setAttribute('aria-hidden', 'true');
      el.style.display = '';
    }
  }

  /* ── Recommendations via Section Rendering API ──────────────────────── */
  /*
   * We fetch the theme's existing `product-recommendations` section via the
   * Storefront section rendering endpoint. This means Liquid runs server-side
   * and has full access to variant metafields (powerx.custom_price), the
   * customer object, and cart attributes — so club pricing is rendered
   * correctly without any client-side metafield lookups.
   *
   * Cache strategy: we cache by productId but bust the cache after every cart
   * mutation (rec add, qty change, remove) so the recommendation list always
   * reflects the current cart context. The cache key is stored on the container
   * element as data-loaded so it survives re-renders.
   */

  /* Debounced rec reload prevents duplicate recommendation requests when
   * multiple cart events fire close together. Events collapse into a single
   * reload 600ms after the last one fires.
   */
  let _recReloadTimer = null;
  function scheduleRecReload() {
    clearTimeout(_recReloadTimer);
    _recReloadTimer = setTimeout(function () {
      loadRecommendations(true);
    }, 600);
  }

  function readCardMainPrice(card) {
    const priceMain = card.querySelector('.ka-card-price-main');
    if (!priceMain) return '';

    const symbolEl = priceMain.querySelector('.ka-card-symbol');
    const dollarsEl = priceMain.querySelector('.ka-card-dollars');
    const centsEl = priceMain.querySelector('.ka-card-cents');
    const symbol = symbolEl ? symbolEl.textContent.trim() : '$';
    const dollars = dollarsEl ? dollarsEl.textContent.trim() : '';
    const cents = centsEl ? centsEl.textContent.trim() : '';

    if (!dollars) return priceMain.textContent.replace(/\s+/g, ' ').trim();
    return symbol + dollars + (cents ? '.' + cents : '');
  }

  function escapePriceText(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  function buildCompactPriceHtml(card, clubPrice) {
    const currentPrice = readCardMainPrice(card);
    const compareEl = card.querySelector('.ka-card-rrp-price');
    const comparePrice = compareEl ? compareEl.textContent.replace(/\s+/g, ' ').trim() : '';
    const isClub = Boolean(clubPrice);
    const hasComparison = Boolean(comparePrice && comparePrice !== currentPrice);
    const state = isClub ? 'club' : (hasComparison ? 'sale' : 'regular');

    if (!currentPrice) {
      const originalPriceBox = card.querySelector('.ka-card-price-box');
      return originalPriceBox ? originalPriceBox.outerHTML : '';
    }

    let ariaLabel = 'Price ' + currentPrice;
    if (isClub && hasComparison) {
      ariaLabel = 'Regular price ' + comparePrice + ', club price ' + currentPrice;
    } else if (isClub) {
      ariaLabel = 'Club price ' + currentPrice;
    } else if (hasComparison) {
      ariaLabel = 'Regular price ' + comparePrice + ', sale price ' + currentPrice;
    }

    let html = '<div class="ka-compact-price ka-compact-price--' + state + '" aria-label="' + escapePriceText(ariaLabel) + '">';
    if (hasComparison) {
      html += '<span class="ka-compact-price__compare" aria-hidden="true">' + escapePriceText(comparePrice) + '</span>';
    }
    html += '<span class="ka-compact-price__current" aria-hidden="true">' + escapePriceText(currentPrice) + '</span>';
    if (isClub) {
      html += '<span class="ka-compact-price__club-badge" aria-hidden="true">CLUB</span>';
    }
    html += '</div>';
    return html;
  }

  async function loadRecommendations(forceReload) {
    const container = document.querySelector('[data-cart-recommendations]');
    if (!container) return;

    const productId = container.dataset.productId;
    if (!productId) return;

    // Skip if already loaded for this product and not forced
    if (!forceReload && container.dataset.loaded === productId) return;

    try {
      const url = ROOT + 'recommendations/products'
        + '?product_id=' + productId
        + '&limit=5'
        + '&intent=related'
        + '&section_id=product-recommendations';

      const res = await fetch(url, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' }
      });
      if (!res.ok) return;

      const html = await res.text();

      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');
      const cards = doc.querySelectorAll('product-card');

      if (!cards.length) {
        container.innerHTML = '';
        return;
      }

      const titleHtml = '<h3 class="ka-cart-recommendations__title">We also recommend</h3>';
      const items = [];

      cards.forEach(function (card) {
        const imgEl = card.querySelector('.product-card-image-link img');
        const imgSrc = imgEl ? (imgEl.getAttribute('src') || '') : '';
        const imgAlt = imgEl ? (imgEl.getAttribute('alt') || '') : '';

        const titleEl = card.querySelector('.product-card-title');
        const title = titleEl ? titleEl.textContent.trim() : '';
        const href = titleEl ? (titleEl.getAttribute('href') || '#') : '#';

        const atcBtn = card.querySelector('.product-card--add-to-cart-button');
        const productHandle = atcBtn ? (atcBtn.dataset.productHandle || '') : '';
        const clubPrice = atcBtn ? (atcBtn.dataset.clubPrice || '') : '';
        const variantId = atcBtn ? (atcBtn.dataset.variantId || atcBtn.dataset.productId || '') : '';
        const priceHtml = buildCompactPriceHtml(card, clubPrice);

        if (!title || !productHandle) return;

        const gwpWrap = card.querySelector('.ka-card-gwp-mini-wrap');
        const gwpHtml = gwpWrap ? gwpWrap.outerHTML : '';

        items.push(
          '<li class="ka-cart-rec-item">'
          + '<a href="' + href + '" class="ka-cart-rec-item__image-link" tabindex="-1" aria-hidden="true">'
          + (imgSrc
            ? '<img src="' + imgSrc + '" alt="' + imgAlt.replace(/"/g, '&quot;') + '" width="64" height="64" loading="lazy" class="ka-cart-rec-item__image">'
            : '<div class="ka-cart-rec-item__image"></div>')
          + '</a>'
          + '<div class="ka-cart-rec-item__info">'
          + '<a href="' + href + '" class="ka-cart-rec-item__title">' + title.replace(/</g, '&lt;').replace(/>/g, '&gt;') + '</a>'
          + gwpHtml
          + priceHtml
          + '</div>'
          + '<button type="button" class="ka-cart-rec-item__add-btn" data-rec-add'
          + ' data-product-handle="' + productHandle + '"'
          + (clubPrice ? ' data-club-price="' + clubPrice + '"' : '')
          + (variantId ? ' data-variant-id="' + variantId + '"' : '')
          + ' aria-label="Add ' + title.replace(/"/g, '&quot;') + ' to cart">Add</button>'
          + '</li>'
        );
      });

      if (!items.length) {
        container.innerHTML = '';
        return;
      }

      container.innerHTML = titleHtml + '<ul class="ka-cart-rec-list">' + items.join('') + '</ul>';
      container.dataset.loaded = productId;

    } catch (err) {
      // Non-fatal — recommendations are a nice-to-have, not mission-critical
      container.innerHTML = '';
    }
  }

  /* ── Quantity +/- button handler ────────────────────────────────────── */
  /*
   * The theme's CartDrawer.onChange() fires when a .qty input's value changes.
   * Our +/- buttons update the input value and dispatch a native "change" event,
   * which the theme's handler picks up — no duplicate AJAX calls needed.
   * After the theme re-renders the drawer, we force-reload recommendations so
   * the list reflects the updated cart context.
   */

  function handleQtyButtonClick(e) {
    const incBtn = e.target.closest('[data-qty-inc]');
    const decBtn = e.target.closest('[data-qty-dec]');
    if (!incBtn && !decBtn) return;

    const selector = e.target.closest('.ka-cart-item__qty-selector');
    if (!selector) return;

    const input = selector.querySelector('.ka-cart-item__qty-input');
    if (!input) return;

    const current = parseInt(input.value, 10) || 0;
    const newVal = incBtn ? current + 1 : Math.max(0, current - 1);

    input.value = newVal;
    input.dispatchEvent(new Event('change', { bubbles: true }));

    // Bust the rec cache after a qty change so recs reload with updated context
    const recContainer = document.querySelector('[data-cart-recommendations]');
    if (recContainer) delete recContainer.dataset.loaded;
  }

  /* ── Recommendation "Add" button ────────────────────────────────────── */
  /*
   * Robustness improvements over v3:
   *
   * 1. Variant ID is now read from data-variant-id on the button (set by the
   *    server-rendered product card) — no second product JSON fetch needed.
   *    Falls back to fetching /products/{handle}.json only if data-variant-id
   *    is absent (e.g. older cached HTML).
   *
   * 2. User-visible error message shown for 3s if the add fails, rather than
   *    silently resetting.
   *
   * 3. Checkout button is disabled during the add to prevent double-submission.
   *
   * 4. Recommendations are force-reloaded after a successful add so the list
   *    updates immediately without requiring a page reload.
   *
   * 5. Loading overlay is shown/hidden around the entire operation.
   */

  async function resolveVariantId(productHandle) {
    const res = await fetch(ROOT + 'products/' + productHandle + '.json', {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!res.ok) throw new Error('Product fetch failed: ' + res.status);
    const data = await res.json();
    const variant = (data.product.variants || []).find(function (v) {
      return v.available !== false;
    }) || (data.product.variants || [])[0];
    if (!variant) throw new Error('No available variant for ' + productHandle);
    return variant.id;
  }

  async function handleRecAdd(btn) {
    if (btn.disabled) return;

    const productHandle = btn.dataset.productHandle;
    if (!productHandle) return;

    btn.disabled = true;
    const originalText = btn.textContent;
    btn.textContent = '...';

    // Disable checkout button during the add to prevent double-submission
    const checkoutBtn = document.querySelector('.ka-cart-checkout-btn');
    if (checkoutBtn) checkoutBtn.setAttribute('aria-disabled', 'true');

    showLoading();

    try {
      // Prefer the variant ID baked into the button by the server-rendered card.
      // Fall back to a product JSON fetch only if it's missing.
      let variantId = btn.dataset.variantId ? parseInt(btn.dataset.variantId, 10) : null;
      if (!variantId) {
        variantId = await resolveVariantId(productHandle);
      }

      const clubPrice = btn.dataset.clubPrice || '';

      const cartDrawer = document.getElementById('Cart-Drawer');
      const sectionsToRender = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map(function (s) { return s.section; })
        : ['cart-drawer', 'cart-bubble'];

      const formData = new FormData();
      formData.append('id', variantId);
      formData.append('quantity', 1);
      if (clubPrice) {
        formData.append('properties[_powerx_custom_price]', clubPrice);
      }
      formData.append('sections', sectionsToRender.join(','));
      formData.append('sections_url', window.location.pathname);

      const res = await fetch(ROOT + 'cart/add.js', {
        method: 'POST',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'Accept': 'application/javascript',
        },
        body: formData,
      });

      const data = await res.json();

      // Shopify returns { status: 422, ... } for errors like sold-out
      if (data.status) {
        const msg = data.description || data.message || 'Could not add item';
        btn.textContent = msg.length > 18 ? 'Unavailable' : msg;
        setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 3000);
        return;
      }

      // Re-render the drawer sections
      if (cartDrawer && data.sections) {
        cartDrawer.getSectionsToRender().forEach(function (section) {
          const el = document.getElementById(section.id);
          if (!el || !data.sections[section.section]) return;
          const target = el.querySelector(section.selector) || el;
          const parser = new DOMParser();
          const doc = parser.parseFromString(data.sections[section.section], 'text/html');
          const selected = doc.querySelector(section.selector);
          if (selected) target.innerHTML = selected.innerHTML;
        });
        if (typeof cartDrawer.removeProductEvent === 'function') {
          cartDrawer.removeProductEvent();
        }
      }

      // Dispatch Standard Storefront Event for analytics integrations
      const se = getSE();
      if (se && se.CartLinesUpdateEvent) {
        try {
          const cartRes = await fetch(ROOT + 'cart.js');
          const cart = await cartRes.json();
          const deferred = se.CartLinesUpdateEvent.createPromise();
          if (cartDrawer) {
            cartDrawer.dispatchEvent(new se.CartLinesUpdateEvent({
              action: 'add',
              context: 'dialog',
              lines: [{ merchandiseId: String(variantId), quantity: 1 }],
              promise: deferred.promise,
            }));
          }
          deferred.resolve({ cart: se.CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
        } catch (e) { /* non-fatal */ }
      }

      btn.textContent = 'Added!';

      // Force-reload recommendations immediately so the list updates without a
      // page reload. We use a short delay to let the section re-render settle.
      setTimeout(function () {
        loadRecommendations(true);
      }, 200);

      setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 1500);

    } catch (err) {
      // Network error or unexpected failure — show a message, never silent-fail
      btn.textContent = 'Error — try again';
      setTimeout(function () { btn.textContent = originalText; btn.disabled = false; }, 3000);
    } finally {
      hideLoading();
      const checkoutBtnFinal = document.querySelector('.ka-cart-checkout-btn');
      if (checkoutBtnFinal) checkoutBtnFinal.removeAttribute('aria-disabled');
    }
  }

  /* ── Safe close helper ──────────────────────────────────────────────── */
  /*
   * The inline onclick handlers on the overlay, close button, and continue
   * shopping button call CartDrawer.close() directly. If the custom element
   * definition hasn't registered yet (extremely unlikely but possible on slow
   * connections), this would throw. We expose a safe global wrapper instead.
   */

  window.kaCartClose = function () {
    const el = document.getElementById('Cart-Drawer');
    if (el && typeof el.close === 'function') {
      el.close();
    }
  };

  /* ── Checkout button — prevent double-tap navigation ───────────────── */

  function guardCheckoutButton() {
    document.addEventListener('click', function (e) {
      const btn = e.target.closest('.ka-cart-checkout-btn');
      if (!btn) return;
      if (btn.getAttribute('aria-disabled') === 'true') {
        e.preventDefault();
        return;
      }
      // Disable briefly after first click to prevent double-tap on mobile
      btn.setAttribute('aria-disabled', 'true');
      // Re-enable after 3s in case the navigation is cancelled
      setTimeout(function () { btn.removeAttribute('aria-disabled'); }, 3000);
    });
  }

  /* ── Event listeners ────────────────────────────────────────────────── */

  function attachListeners() {
    // Qty +/- buttons (delegated — survives re-renders)
    document.addEventListener('click', handleQtyButtonClick);

    // Recommendation "Add" buttons (delegated)
    document.addEventListener('click', function (e) {
      const addBtn = e.target.closest('[data-rec-add]');
      if (addBtn) handleRecAdd(addBtn);
    });

    // Bundle toggle buttons (delegated — survives re-renders)
    // Components are server-rendered by Liquid — the toggle only shows/hides.
    document.addEventListener('click', function (e) {
      const toggleBtn = e.target.closest('[data-bundle-toggle]');
      if (!toggleBtn) return;

      const listId = toggleBtn.getAttribute('aria-controls');
      const list = document.getElementById(listId);
      if (!list) return;

      const isExpanded = toggleBtn.getAttribute('aria-expanded') === 'true';
      toggleBtn.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
      list.hidden = isExpanded;
    });

    // Load recommendations when the drawer opens.
    document.addEventListener('cart-drawer:open', function () {
      dispatchCartViewEvent();
      loadRecommendations();
    });

    // Force-reload recommendations after a successful native cart addition so
    // the list always reflects the current cart context. The debounced reload
    // prevents duplicate recommendation requests when events occur rapidly.
    document.addEventListener('cart:item-added', scheduleRecReload);

    // Guard the checkout button against double-tap
    guardCheckoutButton();
  }

  /* ── MutationObserver: watch for rec container replacement ─────────── */
  /*
   * A section re-render while the drawer is already open replaces the drawer's
   * inner HTML with fresh server-rendered Liquid. This creates a new
   * [data-cart-recommendations] container with data-loaded unset and innerHTML
   * empty. Neither cart-drawer:open nor cart:item-added is guaranteed to fire.
   *
   * A MutationObserver on the Cart-Drawer element detects the new container
   * when it appears and schedules a recommendation reload, regardless of which
   * native cart interaction triggered the re-render.
   */
  function watchForRecContainerReplacement() {
    const cartDrawerEl = document.getElementById('Cart-Drawer');
    if (!cartDrawerEl) return;

    let _lastRecContainer = document.querySelector('[data-cart-recommendations]');

    const observer = new MutationObserver(function () {
      const newRec = document.querySelector('[data-cart-recommendations]');
      // A new container appeared (re-render happened) and it has no data-loaded
      if (newRec && newRec !== _lastRecContainer && !newRec.dataset.loaded) {
        _lastRecContainer = newRec;
        // Small delay to let the re-render fully settle before fetching
        setTimeout(function () { loadRecommendations(true); }, 150);
      }
    });

    observer.observe(cartDrawerEl, { childList: true, subtree: true });
  }

  /* ── Init ───────────────────────────────────────────────────────────── */

  function init() {
    attachListeners();
    watchForRecContainerReplacement();

    // Pre-load recommendations if cart already has items on page load
    const recContainer = document.querySelector('[data-cart-recommendations]');
    if (recContainer && recContainer.dataset.productId) {
      loadRecommendations();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
